import classNames from "classnames";
import {useState} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";

import {BrandDetectButton} from "./BrandDetectButton.tsx";
import {BrandLookupStatus, useBrandLookup, WEBSITE_URL_FIELD} from "./useBrandLookup.ts";

import styles from "./BrandAutofillField.module.scss";

const FIELD_NAME = WEBSITE_URL_FIELD;

const STATUS_MESSAGE: Partial<Record<BrandLookupStatus, string>> = {
  applied: "Available brand assets filled from your website.",
  empty: "We couldn't find brand assets for this URL.",
  error: "We couldn't auto-detect brand assets. Check the URL and try again.",
};

// Website URL field for the status page Design tab. On first visit the field is highlighted to
// surface Brandfetch auto-detection; once the user has entered a URL it settles into a plain field
// with the "Auto-detects brand assets" trigger beside its label. Behind the STATUS_PAGE_BRAND_AUTOFILL flag.
export const BrandAutofillField = () => {
  const {getValues, watch} = useFormContext();
  const {status, queriedUrl, detect} = useBrandLookup();
  const urlValue = watch(FIELD_NAME);
  const [hasInteracted, setHasInteracted] = useState(false);

  const currentUrl = (urlValue || "").trim();
  const isEmpty = !currentUrl;
  const showHighlight = !hasInteracted && isEmpty;
  const isErrorStatus = status === "empty" || status === "error";
  // Only show the status once its lookup URL still matches the field — editing the URL invalidates it.
  const statusMessage = queriedUrl === currentUrl ? STATUS_MESSAGE[status] : undefined;

  const onDetect = () => {
    const value = (getValues(FIELD_NAME) || "").trim();
    if (value) {
      void detect(value);
    }
  };

  const onBlur = () => {
    if ((getValues(FIELD_NAME) || "").trim()) {
      setHasInteracted(true);
    }
  };

  return (
    <div className={classNames(styles.field, {[styles.highlight]: showHighlight})}>
      <div className={styles.labelRow}>
        <FormLabel forId={FIELD_NAME}>Website URL</FormLabel>
        <BrandDetectButton status={status} onDetect={onDetect} />
      </div>
      <InputField id={FIELD_NAME} name={FIELD_NAME} helpText={"Your company's website/URL."} options={{onBlur}} />
      {statusMessage && (
        <span
          role={isErrorStatus ? "alert" : "status"}
          className={classNames(styles.statusMessage, {[styles.statusError]: isErrorStatus})}
        >
          {statusMessage}
        </span>
      )}
    </div>
  );
};
